import { sendEventMetric } from "@expert/cloudwatch-metrics";
import { isKnownPartner } from "@expert/shared-types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useRef } from "react";

import type { EligibilityRequest } from "../../../types";

import { getEligibility } from "../../../api/eligibility";
import { isInvalidMdnError } from "../../../api/eligibility.errors";
import { useSalesContext } from "../../../sales-context/SalesContext";
import { isInternationalCaller } from "../../../shared-utils/formatters/validatePhoneNumber";
import { isVmpSalesEnabled } from "../../../shared-utils/isVmpSalesEnabled";
import { useCallDetailsStore, useProductOfferStore, useVerifiedCustomer } from "../../../stores";
import { parsePhoneNumberAttributes } from "../../product-sale-flow/utils/parsePhoneNumberAttributes";

const QUERY_KEY = "sales-eligibility";
const EXPERT_UNIVERSITY_SUFFIX = "test-training";

export { QUERY_KEY };

export function useEligibility(isEnabled = true) {
    const { sessionId, partner, logger, analytics, taskType, taskData, apiClients, expertLocation, features, ivrInfo } =
        useSalesContext();
    const customer = useVerifiedCustomer(sessionId);
    const hasProducts = useProductOfferStore((state) => (state.getProducts(sessionId)?.length ?? 0) > 0);

    const callDetails = useCallDetailsStore((state) => state.callDetails);
    // Left undefined when channel-share data hasn't supplied PROTECTED_SALES, so the API can tell
    // "not known" apart from "not protected" (it narrows VMP-flagged calls on an unknown value).
    const isCustomerInProtectedState =
        callDetails.PROTECTED_SALES === undefined ? undefined : callDetails.PROTECTED_SALES === "1";
    const vmpSalesEnabled = isVmpSalesEnabled(ivrInfo);
    const isExpertUniversitySimulatedVoiceTask = sessionId.includes(EXPERT_UNIVERSITY_SUFFIX);
    // Nothing writes customer.metadata.isInternationalCaller today — its only writer was the
    // McAfee-SaaS call-intro flow, now deleted. The ANI fallback is what supplies the attribute;
    // the metadata read stays so a future expert-entered value would still take precedence.
    const isInternational = customer?.metadata?.isInternationalCaller ?? isInternationalCaller(taskData.mdn);
    // An unrecognized Connect/CSD code (e.g. `pvl_the_source`) would only earn a Zod 400 from the
    // API, which the panel surfaces as an EligibilityError; skip it so the expert sees no offers.
    // We skip rather than alias these codes in normalizePartner (pvl_the_source -> thesource):
    // whether those lines should sell that partner's catalog is a product call that hasn't been made.
    const isPartnerKnown = isKnownPartner(partner);

    const {
        data: eligibilityResult,
        error,
        isError,
        isLoading,
    } = useQuery({
        queryKey: [
            QUERY_KEY,
            sessionId,
            customer?.mdn,
            customer?.accountNumber,
            // NOTE: email is intentionally NOT in the queryKey. Adding it (PR #6121,
            // ported from legacy PR #6037) caused useEligibility to re-fetch when
            // setCustomer wrote email, racing useCollectCustomerInfo's call inside
            // pDebounce(eligibility, 500ms). The trailing-edge fire used
            // useEligibility's no-firstName/lastName body, which sales-domain
            // Object.assign'd over the session's name fields, breaking smart-link
            // generation. See RDESQ-2032 (legacy fix shipped in PR #6178).
            customer?.currentSubscriptions,
            // callDetails.CLIENT and PROTECTED_SALES ARE in the queryKey (unlike the fields above):
            // they're eligibilityAttributes inputs the backend uses to shape the response (e.g.
            // Verizon IBC/OFR primary-card treatment, VMP narrowing for protected states), and they
            // can legitimately arrive after the first eligibility check fires (channel-share data
            // lands after customer verification). Without them here, staleTime: Infinity means a
            // late value never triggers a refetch, so the backend never gets a chance to reshape
            // the offer for it.
            callDetails.CLIENT,
            isCustomerInProtectedState,
            // Read synchronously from the Connect contact, so it never arrives late. It's here
            // because it is also an eligibilityAttributes input that shapes the response.
            vmpSalesEnabled,
            // In the key for the same reason: chat MDNs decrypt asynchronously, so the ANI-derived
            // value can flip after the first check and must refetch rather than stay cached.
            isInternational,
        ],
        queryFn: async () => {
            logger.info("Checking eligibility");
            return getEligibility(
                apiClients.sales,
                logger,
                analytics,
                {
                    asurionCallId: taskType.isVoice ? taskData.asurionCallId : undefined,
                    partner,
                    sessionId,
                    customerInteractionId: taskData.customerInteractionId,
                    reservationSid: taskData.reservationSid ?? sessionId,
                    ...(isExpertUniversitySimulatedVoiceTask
                        ? { customerMdn: taskData.mdn }
                        : { ...parsePhoneNumberAttributes(customer?.mdn) }),
                    address: customer?.address,
                    customer: {
                        firstName: customer?.firstName,
                        lastName: customer?.lastName,
                        email: customer?.email,
                        accountNumber: customer?.accountNumber,
                    },
                    eligibilityAttributes: {
                        currentPlan: customer?.productName,
                        isInternationalCaller: isInternational,
                        chatSourceApp: taskType.isChat ? taskData.chatSourceApp : undefined,
                        currentSubscriptions: customer?.currentSubscriptions,
                        expertLocation,
                        isCustomerInProtectedState,
                        client: callDetails.CLIENT,
                        isVmpSalesEnabled: vmpSalesEnabled,
                    },
                    salesChannel: taskData.salesChannel as EligibilityRequest["salesChannel"],
                },
                features.isUsingMovedDiscoveryAndEngagementQuestions,
            );
        },
        // Do NOT gate on `!hasProducts`: that made the query sticky for the whole session, so a
        // change to an eligibility input (mdn/account/subscriptions, all in the queryKey) never
        // refreshed the offers and the expert could enroll a stale/ineligible offer. react-query's
        // queryKey + `staleTime: Infinity` already dedupes repeat checks for unchanged inputs, so
        // dropping the gate refreshes on input change without adding calls in the common case.
        enabled: isEnabled && isPartnerKnown && !!customer?.mdn,
        staleTime: Infinity,
        gcTime: Infinity,
        // An invalid MDN is deterministic — retrying won't change the result, and the UI
        // handles it with an inline correction view. Other failures still get one retry.
        retry: (failureCount, queryError) => (isInvalidMdnError(queryError) ? false : failureCount < 1),
    });

    useEffect(() => {
        if (eligibilityResult) {
            const store = useProductOfferStore.getState();
            store.updateProducts(sessionId, eligibilityResult.productConfigs, eligibilityResult.displayCues);
            if (eligibilityResult.meta) {
                store.updateEligibilityMetaBySessionId(sessionId, eligibilityResult.meta);
            }
        }
    }, [eligibilityResult, sessionId]);

    // Surface the cases where the eligibility query would have fired but a gate blocked it:
    // an unrecognized partner, or an empty customer.mdn (the FIOS BAN-flow failure mode:
    // BAN lookup didn't return an mdn and the agent left the form blank).
    const logGateBlock = useEffectEvent((reason: string) => {
        logger.warn({ partner, sessionId, reason }, "Eligibility query disabled");
    });
    // Warn at most once per session — the gate condition can toggle as the agent
    // works the panel, and we only want to flag the session, not every re-render.
    const gateBlockLoggedForSession = useRef<string | null>(null);
    useEffect(() => {
        if (!isEnabled || hasProducts || gateBlockLoggedForSession.current === sessionId) {
            return;
        }
        if (!isPartnerKnown) {
            gateBlockLoggedForSession.current = sessionId;
            logGateBlock("unknown partner");
            // Before this gate an unknown partner 400'd and paged via EligibilityError; keep the
            // skip countable so a partner dropped from `partners` doesn't fail silently.
            void sendEventMetric("EligibilitySkippedUnknownPartner", { partner });
        } else if (!customer?.mdn) {
            gateBlockLoggedForSession.current = sessionId;
            logGateBlock("missing customer.mdn");
        }
    }, [isEnabled, hasProducts, isPartnerKnown, customer?.mdn, sessionId]);

    return { eligibilityResult, error, isError, isLoading };
}

export function clearEligibilityQueryCache(
    queryClient: { removeQueries: (opts: { predicate: (query: { queryKey: readonly unknown[] }) => boolean }) => void },
    sessionId: string,
) {
    queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes(QUERY_KEY) && query.queryKey.includes(sessionId),
    });
}
